import { ArrowRight, Building2, Users, TrendingUp, Clock, DollarSign, CheckCircle, Zap, Target, Phone, Mic, Volume2, Headphones } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { GenieAvatar } from '@/components/GenieAvatar';
import { createBreadcrumbSchema, createCollectionPageSchema, createOrganizationSchema } from '@/config/seo';
import { getSeededGenieDefaultImage } from '@/data/genie/defaults';
import HomeNav from '../Home/HomeNav';
import HomeFooter from '../Home/HomeFooter';
import { getAllBusinessScenarios } from '@/data/business/registry';
import { getCanonicalUrl } from '@/config/site';

export default function BusinessLanding() {
  const scenarios = getAllBusinessScenarios();
  const canonicalUrl = getCanonicalUrl('/business');
  const structuredData = [
    createBreadcrumbSchema([
      { name: 'Home', url: getCanonicalUrl('/') },
      { name: 'Business', url: canonicalUrl },
    ]),
    createCollectionPageSchema(
      'Business Voice AI Solutions',
      'Browse Help Genie voice AI solutions for startups, small businesses, and enterprise teams across sales, support, and customer conversations.',
      canonicalUrl,
    ),
    createOrganizationSchema(),
  ];
  
  // Group scenarios by size for organized display
  const businessSizes = [
    {
      id: 'startup',
      icon: Zap,
      headline: 'For Fast-Growing Startups',
      description: 'Scale customer engagement without scaling headcount. Handle 10x volume with voice automation.',
      image: 'smart-assistance',
      scenarios: scenarios.filter(s => s.meta.size === 'startup')
    },
    {
      id: 'small',
      icon: Target,
      headline: 'For Small Businesses',
      description: 'Compete with enterprise-level service. Professional voice solutions that customers love.',
      image: 'sales-opportunities',
      scenarios: scenarios.filter(s => s.meta.size === 'small')
    },
    {
      id: 'medium',
      icon: TrendingUp,
      headline: 'For Medium Businesses',
      description: 'Optimize operations and unlock growth. Voice technology that scales with your ambitions.',
      image: 'seamless-integration',
      scenarios: scenarios.filter(s => s.meta.size === 'medium')
    },
    {
      id: 'enterprise',
      icon: Building2,
      headline: 'For Enterprises',
      description: 'Enterprise-grade security and compliance. Deploy voice solutions across your organization.',
      image: 'analytics-dashboard',
      scenarios: scenarios.filter(s => s.meta.size === 'enterprise')
    }
  ];

  return (
    <div className="min-h-screen w-full flex flex-col bg-[#F9FAFC]">
      <Helmet>
        <title>Business Voice AI Solutions | Help Genie</title>
        <meta
          name="description"
          content="Browse Help Genie business voice AI solutions for startups, small businesses, and enterprise teams looking to automate support, sales, and call handling."
        />
        <meta
          name="keywords"
          content="business voice ai, ai call handling, voice ai for startups, voice ai for small business, enterprise voice automation"
        />
        <link rel="canonical" href={canonicalUrl} />
        {structuredData.map((entry, index) => (
          <script key={`business-landing-structured-data-${index}`} type="application/ld+json">
            {JSON.stringify(entry)}
          </script>
        ))}
      </Helmet>
      <HomeNav />
      
      <main className="flex-1 flex flex-col items-center justify-center w-full">
        {/* Hero Section with Background Image */}
        <section className="relative w-full min-h-[600px] flex items-center justify-center text-white">
          {/* Background Image */}
          <div className="absolute inset-0 z-0">
            <img src="/analytics-dashboard.webp" alt="Business solutions background" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-blue-900/80 to-purple-900/80 z-10"></div>
          </div>

          {/* Content */}
          <div className="relative z-20 w-full max-w-screen-xl mx-auto px-8 py-20 md:py-32">
            <div className="text-center">
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
                Voice Solutions That
                <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-300 to-purple-300">
                  Scale With You
                </span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-200 mb-10 max-w-3xl mx-auto">
                From startups to enterprises—deploy intelligent voice assistants that delight customers and drive growth
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6">
                <a
                  href="/live-agent/business-onboarding"
                  className="px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 text-lg w-full sm:w-auto text-center"
                >
                  Start Free Trial
                  <ArrowRight className="inline ml-2 h-5 w-5" />
                </a>
                <a
                  href="/live-agent/business-onboarding"
                  className="text-gray-200 hover:text-white font-medium text-lg transition-colors duration-300"
                >
                  Try a Voice Demo &gt;
                </a>
              </div>
              <div className="mt-6 flex flex-wrap justify-center gap-x-5 gap-y-3 text-sm text-gray-200">
                <a href="/help-genie-for-business-solutions" className="hover:text-white transition-colors">
                  Explore solutions
                </a>
                <a href="/pricing" className="hover:text-white transition-colors">
                  View pricing
                </a>
                <a href="/contact" className="hover:text-white transition-colors">
                  Contact sales
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* How Voice Solutions Work Section */}
        <section className="w-full py-16 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                How Business Voice Solutions Work
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Your customers connect through phone or web. Our voice technology handles everything naturally, 
                scaling with your business needs.
              </p>
            </div>

            {/* Voice Demo Visualization */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
              {/* Left: Genie Animation */}
              <div className="flex justify-center">
                <div className="relative p-8 bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl shadow-xl">
                  <div className="text-center">
                    <GenieAvatar 
                      speaking={true} 
                      listening={false} 
                      color="#9333ea"
                      secondaryColor="#2563eb"
                      gradientEnabled={true}
                      customAvatarUrl={getSeededGenieDefaultImage('business-landing')}
                    />
                    <div className="mt-6 space-y-4">
                      {/* Voice Indicator Bars */}
                      <div className="flex justify-center gap-1">
                        {[...Array(7)].map((_, i) => (
                          <div
                            key={i}
                            className="w-2 bg-gradient-to-t from-blue-500 to-purple-500 rounded-full transition-all duration-300 animate-pulse"
                            style={{
                              height: `${Math.random() * 30 + 20}px`,
                              animationDelay: `${i * 0.1}s`
                            }}
                          />
                        ))}
                      </div>
                      <p className="text-sm text-gray-600 font-medium">
                        Experience our business voice solution
                      </p>
                      <a
                        href="/live-agent/business-onboarding"
                        className="inline-block px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-full hover:shadow-lg transition-all duration-300"
                      >
                        Play Voice Demo
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right: How It Works Steps */}
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-blue-100 rounded-lg flex-shrink-0">
                    <Phone className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Multi-Channel Access</h3>
                    <p className="text-gray-600">Customers reach you via phone, website, or app—all unified</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-blue-100 rounded-lg flex-shrink-0">
                    <Mic className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Natural Conversations</h3>
                    <p className="text-gray-600">Voice assistants speak naturally, understanding context and intent</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-blue-100 rounded-lg flex-shrink-0">
                    <Headphones className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Smart Routing</h3>
                    <p className="text-gray-600">Complex issues seamlessly escalate to human agents when needed</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-blue-100 rounded-lg flex-shrink-0">
                    <CheckCircle className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Business Results</h3>
                    <p className="text-gray-600">Reduce costs, increase satisfaction, capture more opportunities</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* All Business Solutions Section */}
        <section className="py-20 md:py-32 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Choose Your Business Solution
              </h2>
              <p className="text-xl text-gray-600">
                Tailored voice technology for every business size and growth stage. Deploy in days, see results immediately.
              </p>
            </div>

            {/* Solutions by Business Size */}
            <div className="space-y-32">
              {businessSizes.filter(size => size.scenarios.length > 0).map((sizeGroup, groupIndex) => (
                <div key={sizeGroup.id} className="scroll-mt-20" id={sizeGroup.id}>
                  {/* Size Header with Image */}
                  <div className="mb-12">
                    <div className="flex flex-col lg:flex-row items-center gap-8 bg-white rounded-2xl p-6 lg:p-8 shadow-lg">
                      {/* Image - Smaller and More Integrated */}
                      <div className="w-full lg:w-1/3 flex-shrink-0">
                        <img 
                          src={`/${sizeGroup.image}.webp`}
                          alt={sizeGroup.headline}
                          className="w-full h-48 lg:h-56 object-cover rounded-xl"
                        />
                      </div>
                      
                      {/* Text Content */}
                      <div className="flex-1">
                        <div className="flex items-start gap-4">
                          <div className="p-3 bg-gradient-to-br from-blue-100 to-purple-100 rounded-2xl flex-shrink-0">
                            <sizeGroup.icon className="w-8 h-8 text-blue-600" />
                          </div>
                          <div>
                            <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-3">
                              {sizeGroup.headline}
                            </h3>
                            <p className="text-lg text-gray-600">
                              {sizeGroup.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Scenarios Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {sizeGroup.scenarios.map((scenario) => (
                      <div
                        key={scenario.meta.id}
                        className="bg-white rounded-xl border-2 border-gray-200 hover:border-blue-400 overflow-hidden hover:shadow-xl transition-all duration-300 group"
                      >
                        <div className="p-8 space-y-4">
                          {/* Voice Indicator at Top */}
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2 text-blue-600">
                              <Volume2 className="w-5 h-5" />
                              <span className="text-xs font-semibold uppercase tracking-wider">Voice Solution</span>
                            </div>
                            <div className="flex gap-1">
                              {[...Array(3)].map((_, i) => (
                                <div key={i} className="w-1 h-3 bg-blue-400 rounded-full animate-pulse" 
                                     style={{ animationDelay: `${i * 0.2}s` }} />
                              ))}
                            </div>
                          </div>

                          {/* Scenario Header */}
                          <div>
                            <h4 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                              {scenario.meta.name}
                            </h4>
                            <p className="text-sm font-medium text-blue-600 mb-4">
                              {scenario.meta.tagline}
                            </p>
                          </div>

                          {/* Business Info */}
                          <div className="flex items-center gap-4 text-xs text-gray-500 pb-3 border-b">
                            <span>{scenario.meta.employeeCount}</span>
                            <span>•</span>
                            <span>{scenario.meta.annualRevenue}</span>
                          </div>

                          {/* Key Challenges/Goals */}
                          <div>
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                              Solves:
                            </p>
                            <ul className="space-y-1">
                              {scenario.meta.challenges.slice(0, 3).map((challenge, idx) => (
                                <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                                  <span>{challenge}</span>
                                </li>
                              ))}
                            </ul>
                          </div>

                          {/* Metrics */}
                          {scenario.metrics && (
                            <div className="pt-4 border-t border-gray-100">
                              <div className="grid grid-cols-3 gap-2 text-center">
                                <div>
                                  <p className="text-xs text-gray-500">ROI</p>
                                  <p className="font-bold text-blue-600">{scenario.metrics.averageROI}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-gray-500">Setup</p>
                                  <p className="font-bold text-gray-900">{scenario.metrics.implementationTime}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-gray-500">Savings</p>
                                  <p className="font-bold text-gray-900">{scenario.metrics.costSavings}</p>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* CTA Button */}
                          <div className="pt-4">
                            <a
                              href={`/business/${scenario.meta.slug}`}
                              className="block w-full text-center px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-medium rounded-lg shadow hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-300"
                            >
                              View Solution
                              <ArrowRight className="inline ml-1 h-4 w-4" />
                            </a>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Bottom CTA Section with Image */}
        <section className="relative w-full py-16 md:py-20 bg-white overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img 
              src="/hero-background.webp" 
              alt="CTA background" 
              className="w-full h-full object-cover opacity-30"
            />
          </div>
          
          <div className="relative z-10 max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
            <div className="inline-block p-4 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full mb-6">
              <img src="/platform-icons/helpgenie_logo.png" alt="Help Genie" className="w-16 h-16" />
            </div>
            
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-6 max-w-3xl mx-auto">
              Ready to Scale Your Business with Voice?
            </h2>
            <p className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto">
              Join thousands of businesses using voice technology to transform customer engagement. No credit card required. Deploy in days.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/live-agent/business-onboarding"
                className="px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 text-lg"
              >
                Start Free Trial
                <ArrowRight className="inline ml-2 h-5 w-5" />
              </a>
              <a
                href="/live-agent/business-onboarding"
                className="px-8 py-4 border-2 border-gray-300 hover:border-gray-400 text-gray-700 font-semibold rounded-lg hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-300 text-lg flex items-center justify-center"
              >
                <Phone className="mr-2 h-5 w-5" />
                Try Voice Demo Now
              </a>
            </div>
          </div>
        </section>
      </main>

      <HomeFooter />
    </div>
  );
}
